// src/pages/TrainingSessionsPage.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext.js';
import { useLanguage } from '../context/LanguageContext.js';
import { useTrainingSessions } from '../hooks/useTrainingSessions.js';
import { useConversations } from '../hooks/useConversations.js';

const translations = {
  en: {
    title: 'AI Training Sessions',
    subtitle: 'Train AI assistants from WhatsApp conversations',
    createNew: 'Create Training Session',
    noSessions: 'No training sessions yet',
    createFirst: 'Create your first training session from approved conversations',
    pending: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    applied: 'Applied',
    viewDetails: 'View Details',
    approve: 'Approve & Create Assistant',
    reject: 'Reject',
    conversations: 'conversations',
    processing: 'Processing...',
    created: 'Created',
    newAssistant: 'New Assistant Created',
    // Modal
    modalTitle: 'Create Training Session',
    selectAssistant: 'Select Assistant',
    selectConversations: 'Select Conversations (approved)',
    sessionName: 'Session Name',
    sessionDescription: 'Description (optional)',
    trainingMode: 'Training Mode',
    newPrompt: 'Create New Prompt',
    improveExisting: 'Improve Existing Prompt',
    create: 'Create Session',
    cancel: 'Cancel',
    selectAtLeast: 'Select at least 1 conversation',
    // Detail Modal
    sessionDetails: 'Training Session Details',
    analysis: 'Analysis',
    generatedPrompt: 'Generated Prompt',
    basePrompt: 'Original Prompt',
    editPrompt: 'Edit Prompt',
    reviewNotes: 'Review Notes (optional)',
    close: 'Close',
    approveSuccess: 'Training session approved! New assistant created.',
    rejectSuccess: 'Training session rejected.',
    creating: 'Analyzing conversations and generating prompt...'
  },
  pt: {
    title: 'Sessões de Treinamento IA',
    subtitle: 'Treine assistentes IA a partir de conversas do WhatsApp',
    createNew: 'Criar Sessão de Treinamento',
    noSessions: 'Nenhuma sessão de treinamento ainda',
    createFirst: 'Crie sua primeira sessão a partir de conversas aprovadas',
    pending: 'Pendente Revisão',
    approved: 'Aprovada',
    rejected: 'Rejeitada',
    applied: 'Aplicada',
    viewDetails: 'Ver Detalhes',
    approve: 'Aprovar e Criar Assistente',
    reject: 'Rejeitar',
    conversations: 'conversas',
    processing: 'Processando...',
    created: 'Criada',
    newAssistant: 'Novo Assistente Criado',
    // Modal
    modalTitle: 'Criar Sessão de Treinamento',
    selectAssistant: 'Selecionar Assistente',
    selectConversations: 'Selecionar Conversas (aprovadas)',
    sessionName: 'Nome da Sessão',
    sessionDescription: 'Descrição (opcional)',
    trainingMode: 'Modo de Treinamento',
    newPrompt: 'Criar Novo Prompt',
    improveExisting: 'Melhorar Prompt Existente',
    create: 'Criar Sessão',
    cancel: 'Cancelar',
    selectAtLeast: 'Selecione pelo menos 1 conversa',
    // Detail Modal
    sessionDetails: 'Detalhes da Sessão',
    analysis: 'Análise',
    generatedPrompt: 'Prompt Gerado',
    basePrompt: 'Prompt Original',
    editPrompt: 'Editar Prompt',
    reviewNotes: 'Notas de Revisão (opcional)',
    close: 'Fechar',
    approveSuccess: 'Sessão aprovada! Novo assistente criado.',
    rejectSuccess: 'Sessão rejeitada.',
    creating: 'Analisando conversas e gerando prompt...'
  }
};

export default function TrainingSessionsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const {
    sessions,
    loading,
    error,
    createSession,
    fetchSessions,
    getSessionById,
    approveSession,
    rejectSession
  } = useTrainingSessions();

  const { conversations, fetchConversations } = useConversations();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [creating, setCreating] = useState(false);

  // Create form state
  const [assistantId, setAssistantId] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [trainingMode, setTrainingMode] = useState('new_prompt');
  const [selectedConversations, setSelectedConversations] = useState([]);

  // Detail modal state
  const [editedPrompt, setEditedPrompt] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  const t = translations[language] || translations.en;

  const isAuthorized = user?.isAdmin;

  useEffect(() => {
    if (isAuthorized) {
      fetchSessions();
      fetchConversations({ status: 'approved' }); // Only approved conversations
    }
  }, [isAuthorized, fetchSessions, fetchConversations]);

  if (!isAuthorized) {
    return (
      <Container className="py-4">
        <h5>Access Denied - Admin only</h5>
      </Container>
    );
  }

  const handleCreateSession = async () => {
    if (selectedConversations.length === 0) {
      alert(t.selectAtLeast);
      return;
    }

    setCreating(true);
    try {
      await createSession({
        assistantId,
        sessionName,
        sessionDescription,
        trainingMode,
        conversationIds: selectedConversations
      });

      setShowCreateModal(false);
      setSuccessMessage('Training session created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh sessions
      await fetchSessions();

      // Reset form
      setAssistantId('');
      setSessionName('');
      setSessionDescription('');
      setTrainingMode('new_prompt');
      setSelectedConversations([]);
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleViewDetails = async (session) => {
    try {
      const fullSession = await getSessionById(session.id);
      setSelectedSession(fullSession);
      setEditedPrompt(fullSession.generated_prompt);
      setReviewNotes('');
      setShowDetailModal(true);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleApprove = async () => {
    try {
      const result = await approveSession(selectedSession.id, reviewNotes);

      setShowDetailModal(false);
      setSuccessMessage(`${t.approveSuccess} (${result.newAssistant.name})`);
      setTimeout(() => setSuccessMessage(''), 5000);

      await fetchSessions();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleReject = async () => {
    try {
      await rejectSession(selectedSession.id, reviewNotes);

      setShowDetailModal(false);
      setSuccessMessage(t.rejectSuccess);
      setTimeout(() => setSuccessMessage(''), 3000);

      await fetchSessions();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
      applied: 'info'
    };
    return <Badge bg={variants[status] || 'secondary'}>{t[status] || status}</Badge>;
  };

  return (
    <Container fluid style={{ padding: '20px 40px' }}>
      {/* Header */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h3 style={{ fontWeight: 600, color: '#1a1a1a' }}>
            <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>🤖</span>
            {t.title}
          </h3>
          <p style={{ color: '#666', marginBottom: 0 }}>{t.subtitle}</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          ➕ {t.createNew}
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="danger">
          Error: {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && !creating && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {/* No Sessions */}
      {!loading && sessions.length === 0 && (
        <div className="text-center py-5">
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🤖</div>
          <h4 style={{ color: '#666' }}>{t.noSessions}</h4>
          <p style={{ color: '#999' }}>{t.createFirst}</p>
        </div>
      )}

      {/* Sessions List */}
      {!loading && sessions.length > 0 && (
        <Row className="g-3">
          {sessions.map(session => (
            <Col key={session.id} xs={12} lg={6}>
              <Card style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 style={{ marginBottom: '4px' }}>{session.session_name}</h5>
                      {session.session_description && (
                        <small style={{ color: '#666' }}>{session.session_description}</small>
                      )}
                    </div>
                    {getStatusBadge(session.status)}
                  </div>

                  <div className="mb-3">
                    <small style={{ color: '#999' }}>
                      {session.conversation_count} {t.conversations} • {new Date(session.created_at).toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-NZ')}
                    </small>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleViewDetails(session)}
                    >
                      👁️ {t.viewDetails}
                    </Button>

                    {session.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleViewDetails(session)}
                        >
                          ✅ {t.approve}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleViewDetails(session)}
                        >
                          ❌ {t.reject}
                        </Button>
                      </>
                    )}

                    {session.status === 'applied' && session.new_assistant_id && (
                      <Badge bg="info">{t.newAssistant}</Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Create Modal - Will implement next */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t.modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#999', marginBottom: '20px' }}>
            {creating ? t.creating : 'Select conversations to analyze and generate a new AI assistant prompt.'}
          </p>

          {creating ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">{t.creating}</p>
            </div>
          ) : (
            <Form>
              {/* Form fields - simplified for now */}
              <Form.Group className="mb-3">
                <Form.Label>{t.sessionName}</Form.Label>
                <Form.Control
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="Training from WhatsApp - Jan 2025"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{t.selectConversations}</Form.Label>
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                  {conversations.filter(c => c.is_approved_for_training).map(conv => (
                    <Form.Check
                      key={conv.id}
                      type="checkbox"
                      label={`${conv.client_name || conv.phone_number} - ${conv.message_count} messages`}
                      checked={selectedConversations.includes(conv.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedConversations([...selectedConversations, conv.id]);
                        } else {
                          setSelectedConversations(selectedConversations.filter(id => id !== conv.id));
                        }
                      }}
                    />
                  ))}
                </div>
                <small className="text-muted">Selected: {selectedConversations.length}</small>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>
            {t.cancel}
          </Button>
          <Button variant="primary" onClick={handleCreateSession} disabled={creating || selectedConversations.length === 0}>
            {creating ? t.processing : t.create}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Detail Modal - Will implement next */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="xl" fullscreen="lg-down">
        <Modal.Header closeButton>
          <Modal.Title>{t.sessionDetails}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedSession && (
            <div>
              {/* Session Info */}
              <div className="mb-4 pb-3" style={{ borderBottom: '1px solid #e0e0e0' }}>
                <h5>{selectedSession.session_name}</h5>
                <p>{selectedSession.session_description}</p>
                <div className="d-flex gap-2 align-items-center">
                  {getStatusBadge(selectedSession.status)}
                  <small className="text-muted">
                    {selectedSession.conversation_count} conversations •
                    {selectedSession.processing_time_seconds}s processing
                  </small>
                </div>
              </div>

              {/* Generated Prompt (Editable!) */}
              <div className="mb-4">
                <h6>{t.generatedPrompt}</h6>
                <Form.Control
                  as="textarea"
                  rows={15}
                  value={editedPrompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                  disabled={selectedSession.status !== 'pending'}
                />
                <small className="text-muted">{t.editPrompt}</small>
              </div>

              {/* Review Notes */}
              {selectedSession.status === 'pending' && (
                <Form.Group className="mb-3">
                  <Form.Label>{t.reviewNotes}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Optional notes about this training session..."
                  />
                </Form.Group>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            {t.close}
          </Button>
          {selectedSession?.status === 'pending' && (
            <>
              <Button variant="danger" onClick={handleReject}>
                ❌ {t.reject}
              </Button>
              <Button variant="success" onClick={handleApprove}>
                ✅ {t.approve}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
